/**
 * The cert-warning payload, and the words we put on it.
 *
 * ─── ONE SHAPE, TWO MOMENTS ─────────────────────────────────────────────────
 *
 * POST /employee/my-jobs/:id/equipment returns `warnings[]` in BOTH of its
 * interesting outcomes, identically shaped:
 *
 *   409 { success:false, code:'cert_warning', warnings:[…] }  nothing written
 *   200 { success:true,  data:{ equipment:[…], warnings:[…] } } forced, written
 *
 * So one renderer serves the confirm dialog and the post-write toast, and the
 * crew reads the same sentence before and after the decision. If those two ever
 * disagreed, the dialog would be describing a different override than the one
 * that got recorded.
 *
 * ─── WARN, DO NOT BLOCK — AND THAT IS WHY WE BADGE RATHER THAN HIDE ─────────
 *
 * A hand at the shop at 05:00 with one spider four days past its day limit and
 * a well waiting is going to run that spider. The question is only whether the
 * system knows. Blocking produces a phone call and an untracked decision;
 * warning produces a recorded one — the endpoint writes a
 * `cert_override_on_assign` row to `tenant_audit_log`.
 *
 * Filtering out-of-cert gear from the picker would be blocking by the back
 * door: the crew could not make the override the endpoint exists to record, so
 * the gear would go on the truck with no row anywhere. Badge it, let them
 * choose, keep the record.
 *
 * ─── NOTHING HERE DECIDES WHETHER SOMETHING IS OUT OF CERT ──────────────────
 *
 * `out_of_cert` and `reasons` are the server's verdict, from the SAME
 * `certWarningFor` the write path uses. Recomputing it client-side is how a
 * picker comes to disagree with the endpoint it feeds — greying a unit the
 * write accepts, or worse showing one clean that the write then 409s. This file
 * only labels.
 */

/** The two arms the server actually implements. */
export type CertWarningReason = 'cert_expired' | 'day_limit_exceeded';

export interface CertWarning {
  equipment_id: string;
  name: string | null;
  serial_number: string | null;
  reasons: CertWarningReason[];
  cert_expiry_date: string | null;
  cert_day_limit: number | null;
  working_days_since_inspection: number | null;
}

/**
 * Exhaustive map, never a binary check or a raw-value fallback.
 *
 * A reason we do not have a label for is a reason the server added and this
 * screen has not learned yet. It renders as its own text rather than being
 * dropped, because a warning that silently disappears is worse than an ugly
 * one — the crew would assign against a rule nobody showed them.
 */
const REASON_LABEL: Record<CertWarningReason, string> = {
  cert_expired: 'Certificate expired',
  day_limit_exceeded: 'Past working-day limit',
};

export function certReasonLabel(reason: string): string {
  return REASON_LABEL[reason as CertWarningReason] ?? reason.replace(/_/g, ' ');
}

/** "Certificate expired · Past working-day limit", or '' for no reasons. */
export function certReasonSummary(reasons: string[] | null | undefined): string {
  if (!reasons || reasons.length === 0) return '';
  return reasons.map(certReasonLabel).join(' · ');
}

/**
 * The one-line badge for a picker row.
 *
 * Deliberately short — it sits next to the unit name on a phone. The full
 * reasons and dates go in the confirm dialog, where there is room and where the
 * decision is actually being made.
 */
export function certBadgeLabel(reasons: string[] | null | undefined): string {
  if (!reasons || reasons.length === 0) return 'Out of cert';
  if (reasons.length === 1) return certReasonLabel(reasons[0]);
  return 'Out of cert';
}

/**
 * Pull `warnings[]` off either response, without caring which one it was.
 *
 * The 409 body carries it at the top level and the 200 nests it under `data`.
 * `'data' in body ? body.data : body` rather than `body.data || body`, because
 * `{success:true, data:null}` would fall through the `||` to the envelope —
 * which is truthy, so every field would read `undefined` and the caller would
 * decide there were no warnings when it simply had not looked in the right box.
 */
export function warningsFrom(body: any): CertWarning[] {
  if (!body || typeof body !== 'object') return [];
  if (Array.isArray(body.warnings)) return body.warnings;
  const inner = 'data' in body ? body.data : body;
  if (inner && Array.isArray(inner.warnings)) return inner.warnings;
  return [];
}

/**
 * Is this the cert-warning 409, as opposed to any other 409?
 *
 * Keyed on `code`, NEVER on the error string. The string is prose and will be
 * reworded; `code: 'cert_warning'` is the contract. A message-matching check
 * would silently stop offering the override the day somebody fixes a typo, and
 * the crew would see a hard failure where a confirm dialog belongs.
 */
export function isCertWarningResponse(status: number | undefined, body: any): boolean {
  return status === 409 && body?.code === 'cert_warning';
}
